import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'next-i18next'
import { Button, Divider } from '@gate/gui'
import { eventTracker } from '@/utils/collectEvent'
import useLangPrefix from '@/hooks/useLangPrefix'
import { Skeleton, Flex } from '@mantine/core'
import { useStore } from '@/store/index'
import BaseSuspense from '@/components/BaseSuspense'
import BaseEmpty from '@/components/BaseEmpty'
import { deepClone } from '@/utils/tools'

import { PriceLearnStyled, PriceLearnTitle } from './style'

const GateLearnLiItem = React.lazy(() => import('@/blocks/Price/PriceLearn/Item'))

const PriceLearn = () => {
  const { t } = useTranslation()
  const { langPrefix } = useLangPrefix()
  const { priceClientData } = useStore().baseStore

  const { loading, gateLearnListData } = priceClientData

  const gateLearnDealData = useMemo(() => {
    const _data = (deepClone(gateLearnListData) ?? []).sort(
      (a, b) => new Date(b?.updated_at).getTime() - new Date(a?.updated_at).getTime()
    )
    return _data
  }, [gateLearnListData])

  /**
   * @description: gate learn | 骨架屏
   * @param {*} React
   * @return {*}
   */
  const PriceLearnItemSkeleton = ({ showDivider }: { showDivider?: boolean }): React.ReactElement => {
    return (
      <Flex direction="column" gap={16}>
        <Flex gap={8}>
          <Skeleton w={88} h={88} radius={8} miw={88} />
          <Flex direction="column" gap={8} w="100%">
            <Skeleton w={'60%'} h={19} />
            <Skeleton w={'100%'} h={64} />
          </Flex>
        </Flex>
        <Skeleton w={'100%'} h={24} />
        {showDivider ? <Divider type="divider-2" mt={8} mb={24} /> : null}
      </Flex>
    )
  }

  return (
    <PriceLearnStyled>
      <Flex align="center" justify="space-between" mb={24}>
        <PriceLearnTitle>{t('P.GateLearn')}</PriceLearnTitle>
        <a
          onClick={() => {
            eventTracker('price_sectors_click', {
              button_name: 'gate_learn_more',
            })
          }}
          href={`${langPrefix}/learn`}
          target="_blank"
        >
          <Button type="outlined" size="xs" label={t('P.More') as string} className="text-1 size-12 font-500" />
        </a>
      </Flex>

      <BaseSuspense
        loading={loading}
        fallback={
          <>
            <PriceLearnItemSkeleton showDivider={true} />
            <PriceLearnItemSkeleton showDivider={true} />
            <PriceLearnItemSkeleton />
          </>
        }
      >
        <ul>
          {gateLearnListData?.length ? (
            <>
              {gateLearnDealData?.map((item, index) => (
                <GateLearnLiItem borderState={index + 1 !== gateLearnListData?.length} key={item.id} {...item} />
              ))}
            </>
          ) : (
            <Flex py={48} mih={491} justify="center" align="center">
              <BaseEmpty />
            </Flex>
          )}
        </ul>
      </BaseSuspense>
    </PriceLearnStyled>
  )
}

export default observer(PriceLearn)
